import { Tab } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../../../../core/components/atoms/Button';
import Checkbox from '../../../../core/components/atoms/Checkbox';
import Container from '../../../../core/components/atoms/Container';
import { Toast, toast } from '../../../../core/components/atoms/Toast';
import BrainLoadingWrapper from '../../../../core/components/molecules/BrainLoading';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import FontButtons from '../../../../core/components/organism/FontButtons';
import EssayPaper from '../../../../core/components/organism/Paper';
import { Modal } from '../../../../core/components/templates/Modal';
import NoContent from '../../../../core/components/templates/NoContent';
import useDebounce from '../../../../core/hooks/useEssayWriteDebounce';
import { IEssayBody } from '../../../../core/models/essay';
import { useEssayCoreStore } from '../../../../core/stores/essayCoreStore';
import EssaySendFooter from '../../components/EssayFooterSend';
import ModalOnCancel from '../../components/Modal/cancel';
import ModalOnChange from '../../components/Modal/change';
import ModalOnError from '../../components/Modal/error';
import ModalOnSuccess from '../../components/Modal/send';
import ModalOnUnload from '../../components/Modal/unload';
import SelectImage from '../../components/SelectImage';
import WritingHeader from '../../components/WriteHeader';
import { useEssayTheme } from '../../hooks/useEssayTheme';
import { useSaveEssay } from '../../hooks/useSaveEssay';
import { useSendEssay } from '../../hooks/useSendEssay';
import { useUserEssay } from '../../hooks/useUserEssay';
import { useUserEssayStore } from '../../stores/essayByUser';
import { useEssayImageStore } from '../../stores/essayImageStore';
import { meEssayStore } from '../../stores/meEssayStore';
import { useSendEssayStore } from '../../stores/sendEssayStore';
import { sizeConverter } from '../../utils';

export type essayOrigins = 'course' | 'essay' | 'practice' | 'plano-adaptativo';

interface IWriteEssay {
  origin: essayOrigins;
  footerType?: 'embedded' | 'default';
  finish?: () => void;
  onBack?: () => void;
  showBackButton?: boolean;
  slug?: string;
  className?: string;
}

const sizes = {
  default: 16,
  small: 14,
  large: 18,
};

export default function WriteEssay({
  origin = 'essay',
  footerType = 'default',
  finish,
  onBack,
  showBackButton = false,
  slug,
  className = '',
}: IWriteEssay) {
  const { pathname } = useLocation();
  const { data, isError } = useEssayTheme(slug || '');
  const { setEssay } = useUserEssayStore();
  const { data: userEssay, isPending: loadingEssay } = useUserEssay(slug || '', setEssay);

  const { resetEssayImage } = useEssayImageStore();
  const { setText, setImage, setTitle } = useEssayCoreStore();
  const { isSuccess, setIsSuccess } = useSendEssayStore();
  const { credits } = meEssayStore();

  const { mutate: mutateSave, isPending: savePending } = useSaveEssay();
  const {
    mutate: mutateSend,
    isPending,
    isError: sendError,
    data: dataSended,
  } = useSendEssay(setIsSuccess);
  const { setImageURL: setStorageUrl } = useEssayImageStore();

  //Helpers
  const [activeTab, setActiveTab] = useState<number>(0);
  const [essaySent, setEssaySent] = useState<boolean>(false);
  const [nextPage, setNextPage] = useState<number>(0);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isAware, setIsAware] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);

  //Essay
  const [fontSize, setFontSize] = useState<'small' | 'default' | 'large'>(
    'default'
  );
  const [essayText, setEssayText] = useState<string>('');
  const [essayTitle, setEssayTitle] = useState<string>('');

  //Image
  const [imageURL, setImageURL] = useState<string>('');

  //Modais
  const [isOpenCancel, setIsOpenCancel] = useState<boolean>(false);
  const [isOpenChange, setIsOpenChange] = useState<boolean>(false);
  const [isOpenSend, setIsOpenSend] = useState<boolean>(false);
  const [isOpenUnload, setIsOpenUnload] = useState<boolean>(false);
  const [isOpenError, setIsOpenError] = useState<boolean>(false);
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);

  const debouncedInput = useDebounce(essayText);
  const debouncedTitle = useDebounce(essayTitle);

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (
      essayText.length > 0 &&
      !essaySent &&
      !isInitialLoad.current &&
      userEssay?.text !== essayText
    ) {
      saveEssay({ isSending: false });
    }
  }, [debouncedInput]);

  useEffect(() => {
    if (
      essayTitle.length > 0 &&
      !essaySent &&
      !isInitialLoad.current &&
      userEssay?.title !== essayTitle
    ) {
      saveEssay({ isSending: false });
    }
  }, [debouncedTitle]);

  useEffect(() => {
    if (userEssay) {
      if (userEssay.image === null || !userEssay.image) {
        const fontSize = sizeConverter(userEssay.fontSize);

        setEssayText(userEssay.text === null ? '' : userEssay.text);
        setEssaySent(userEssay.sentCorrection);
        setFontSize(fontSize);

        if (userEssay.title) {
          setEssayTitle(userEssay.title);
        }

        resetEssayImage();
      } else {
        setActiveTab(1);
        setImageURL(userEssay.image);
      }
      // Set isInitialLoad to false after initial data is loaded
      isInitialLoad.current = false;
    }
  }, [userEssay]);

  useEffect(() => {
    if (!isPending && dataSended && dataSended.sentCorrection) {
      setIsOpenSend(true);
    }
  }, [isPending, isSuccess]);

  useEffect(() => {
    if (sendError) {
      setIsOpenError(true);
    }
  }, [sendError]);

  const clearError = useCallback(() => {
    setIsOpenError(false);
  }, []);

  const changeTab = useCallback((tab: number) => {
      if (window.innerWidth <= 969) {
        setActiveTab(tab);
      }

      if (
        tab !== activeTab &&
        !isFinished &&
        !essaySent &&
        window.innerWidth > 969
      ) {
        setNextPage(tab);
        setIsOpenChange(true);
      }
    },
    [activeTab, isFinished, essaySent]
  );

  const saveWhenImage = (url: string) => {
    if (!url) {
      return;
    }

    setImageURL(url);

    const body: any = {
      image: url,
      text: null,
      fontSize: sizes['default'],
    };

    saveEssay({ isSending: false, complement: body });
  };

  const clearOnChangeType = () => {
    if (!essaySent) {
      if (nextPage === 1) {
        setEssayText('');
        setEssayTitle('');
        setText(null);
        setTitle(null);
      } else {
        setImageURL('');
        setStorageUrl('');
        setImage(null);
      }

      const body = {
        image: null,
        text: null,
        title: '',
      };

      saveEssay({ isSending: false, complement: body });
    }
  };

  const handleCancelEssay = useCallback(() => {
    setEssayText('');
    setEssayTitle('');
    setImageURL('');
    setImage(null);
    setText(null);
    setTitle(null);
    setStorageUrl('');
    setIsOpenCancel(false);
  }, [setImage, setText, setStorageUrl, setTitle]);

  const finishEssay = () => {
    setIsSuccess(false);
    setIsOpenSend(false);
    setIsFinished(true);
    setImage(null);
    setText(null);
    setTitle(null);
    resetEssayImage();

    if (finish) {
      finish();
    }
  };

  const saveEssay = (props: { isSending: boolean; complement?: any }) => {
    const { isSending, complement } = props;

    if (!data) {
      toast({
        variant: 'error',
        message: 'Erro ao salvar a redação',
        title: 'Erro!',
        position: 'top-right',
      });

      return;
    }

    const formatedText = essayText.trim().replace(/(&lt;|<)br\s*\/?(&gt;|>)/gi, '\n');
    const finalText = formatedText.length === 0 ? null : formatedText;

    if (isSending) {
      if (!imageURL && essayText.length === 0 && formatedText.length === 0) {
        toast({
          variant: 'error',
          message: 'O seu texto está em branco',
          title: 'Erro!',
          position: 'top-right',
        });
        return;
      }

      if (!imageURL && !isValid) {
        toast({
          variant: 'error',
          message: 'O seu texto excede a quantidade de linhas',
          title: 'Erro!',
          position: 'top-right',
        });

        return;
      }
    }

    let body: IEssayBody = {
      image: imageURL ? imageURL : null,
      text: imageURL ? null : finalText,
      theme: data?.themeId || '',
      tryNumber: userEssay?.tryNumber || 0,
      fontSize: sizes[fontSize],
      title: "",
    };

    if (data?.correctionType === 3 || data?.correctionType === 7) {
      if (isSending && essayTitle.length === 0 && !imageURL) {
        toast({
          variant: 'error',
          message: 'O título da redação é obrigatório',
          title: 'Erro!',
          position: 'top-right',
        });

        return;
      }

      body.title = essayTitle;
    }

    if (complement) {
      body = {
        ...body,
        ...complement,
      };
    }

    if (isSending) {
      mutateSend(body);
    } else {
      mutateSave(body);
    }
  };

  const handleSendEssay = useCallback(
    (action: string) => {
      if (action === 'send' && isAware) {
        setEssayText(essayText.trim());
        saveEssay({ isSending: true });
      }
    },
    [isAware, saveEssay]
  );

  const saveFontSize = useCallback(
    (size: 'small' | 'default' | 'large') => {
      setFontSize(size);

      const body: any = {
        fontSize: sizes[size],
      };

      saveEssay({ isSending: false, complement: body });
    },
    [saveEssay, fontSize]
  );

  if (isError) {
    const url = pathname.includes('curso')
      ? `${pathname}/detalhes`
      : `/redacoes/temas`;

    return <NoContent url={url} />;
  }

  return (
    <Container
      direction='col'
      gap='xxl'
      className={`${origin === 'essay' ? 'pt-0' : ''} ${className}`}
    >
      <Toast />
      {origin === 'essay' && (
        <Breadcrumbs
          items={[
            {
              label: 'Temas',
              href: `/redacoes/temas`,
            },
            {
              label: data?.theme || '',
              href: '/redacoes/start/' + slug,
            },
            {
              label: 'Enviar redação',
              href: '#',
            },
          ]}
        />
      )}
      {origin !== 'practice' && <WritingHeader />}
      
      <Tab.Group selectedIndex={activeTab} onChange={changeTab}>
        <Tab.List className='m-auto w-fit'>
          <Tab
            className={`
              ${
                activeTab === 0
                  ? 'border-hairline border-mono-color-medium bg-color-brand-primary'
                  : 'border-hairline border-mono-color-medium bg-color-lightest'
              } 
              rounded-l-pill px-4 py-[10px] ring-0 active:ring-0 xs:w-fit md:w-60
            `}
          >
            <Typography
              type='title'
              className={`${activeTab === 0 ? 'text-button-color-primary-default' : ''} text-center xs:text-h6 md:text-h4`}
            >
              Digitar redação
            </Typography>
          </Tab>
          <Tab
            className={`
              ${
                activeTab === 1
                  ? 'border-hairline border-mono-color-medium bg-color-brand-primary'
                  : 'border-hairline border-mono-color-medium bg-color-lightest'
              } 
              rounded-r-pill px-4 py-[10px] ring-0 active:ring-0 xs:w-fit md:w-60
            `}
          >
            <Typography
              type='title'
              className={`${activeTab === 1 ? 'text-button-color-primary-default' : ''}text-center xs:text-h6 md:text-h4`}
            >
              Enviar imagem
            </Typography>
          </Tab>
        </Tab.List>
        <Tab.Panels className={`pb-20`}>
          <Tab.Panel className='flex flex-col gap-xxxl'>
            <div className='xs:block lg:hidden'>
              <Typography weight='bold' className='text-center'>
                Não é possivel digitar redações por dispositivos tablet e
                mobile.sdaso
              </Typography>
              <Typography className='text-center'>
                Você pode escrever sua redação a mão, tirar uma foto e enviá-la
                através do envio por imagem.
              </Typography>
              <Button
                variant='outlined'
                onClick={() => changeTab(1)}
                className='mx-auto mt-5'
              >
                Enviar redação como imagem
              </Button>
            </div>
            <div className='xs:hidden lg:block'>
              <div className='flex w-full flex-row items-center justify-center'>
                <FontButtons changeFont={saveFontSize} fontSize={fontSize} isBlocked={essaySent || isFinished} />
              </div>
              {loadingEssay ? (
                <BrainLoadingWrapper />
              ) : (
                <EssayPaper
                  loading={savePending}
                  correction={data?.correctionType || 0}
                  fontSize={fontSize}
                  setText={(text: string) => setEssayText(text)}
                  essayText={essayText}
                  setTitle={(title: string) => setEssayTitle(title)}
                  essayTitle={essayTitle}
                  blocked={essaySent || isFinished}
                  setIsValid={setIsValid}
                />
              )}
            </div>
          </Tab.Panel>
          <Tab.Panel className={`flex flex-col`}>
            <SelectImage
              imageURL={imageURL}
              setImage={(url) => setImageURL(url)}
              saveImage={(url) => saveWhenImage(url)}
              essaySent={essaySent}
              correctionType={data?.correctionType}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
      {origin !== 'practice' && (
        <EssaySendFooter
          disabled={
            essaySent ||
            !credits?.remainingCreditsMonth ||
            credits?.remainingCreditsMonth < 1 ||
            isFinished
          }
          onSend={() => setIsOpenConfirmation(true)}
          onCancel={() => setIsOpenCancel(true)}
          isLoading={isPending}
          onBack={onBack}
          showBackButton={showBackButton}
          embedded={footerType === 'embedded'}
        />
      )}
      <ModalOnError
        isOpen={isOpenError}
        onClose={() => clearError()}
        message='Erro ao enviar a redação'
      />
      <ModalOnChange
        onCancel={() => setIsOpenChange(false)}
        onClose={() => setIsOpenChange(false)}
        isOpen={isOpenChange}
        action={() => {
          setIsOpenChange(false);
          setActiveTab(nextPage);
          clearOnChangeType();
        }}
      />
      <ModalOnSuccess
        onClose={() => setIsOpenSend(false)}
        isOpen={isOpenSend}
        action={() => finishEssay()}
      />
      <ModalOnUnload
        onClose={() => setIsOpenUnload(false)}
        isOpen={isOpenUnload}
        action={() => {
          setIsOpenUnload(false);
          setActiveTab(nextPage);
        }}
      />
      <ModalOnCancel
        onClose={() => setIsOpenCancel(false)}
        isOpen={isOpenCancel}
        onCancel={() => setIsOpenCancel(false)}
        action={() => handleCancelEssay()}
      />
      <Modal
        isOpen={isOpenConfirmation}
        onClose={() => {
          setIsAware(false);
          setIsOpenConfirmation(false);
        }}
        position='center'
        className='w-[40%] p-0'
      >
        <Fragment>
          <Typography className='mb-2'>
            A sua redação será enviada para correção e{' '}
            <strong>1 crédito será descontado</strong>.
          </Typography>
          <Checkbox
            onChange={() => setIsAware(!isAware)}
            checked={isAware}
            label='Estou ciente de que minha redação será enviada para correção e de que não é possível desfazer essa ação.'
            id='essay-aware'
            value='essay-aware'
            disabled={credits?.remainingCreditsMonth === 0}
          />
          <div className='flex flex-row items-center justify-end gap-sm'>
            <Button
              disabled={isPending}
              variant='text'
              onClick={() => {
                setIsAware(false);
                setIsOpenConfirmation(false);
                setEssayText(essayText.trim());
              }}
            >
              Cancelar
            </Button>
            <Button
              loading={isPending}
              variant='text'
              disabled={!isAware}
              onClick={() => handleSendEssay('send')}
            >
              Enviar
            </Button>
          </div>
        </Fragment>
      </Modal>
    </Container>
  );
}
